import { Divider, Drawer, Group, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";

type ChargeItemCommentsDrawerProps = {
  opened: boolean;
  onClose: () => void;
  /** The row Notes was clicked on. Read-only display, so `null` while closed is enough. */
  row: EncounterChargeItemRow | null;
};

function ChargeItemNoteSection({
  title,
  note,
  testId,
}: {
  title: string;
  note: string;
  testId: string;
}) {
  return (
    <Stack gap="xs" data-testid={testId}>
      <TextWrapper fw={600} c="gray.7">
        {title}
      </TextWrapper>
      {note ? (
        <TextWrapper style={{ whiteSpace: "pre-wrap" }}>{note}</TextWrapper>
      ) : (
        <TextWrapper c="gray.7">No comments yet.</TextWrapper>
      )}
    </Stack>
  );
}

/**
 * Drawer that shows the Practitioner and Biller notes recorded on one captured charge.
 *
 * Read-only: this is the display half of the design's Notes action. Adding or editing a note from
 * here is not built yet — that goes through Edit Procedure Code.
 *
 * Reads straight off the row's `editTarget`, which carries the charge's note extensions (see
 * `toEditTarget`). The row is already built from the charge, so there is nothing to fetch, and the
 * drawer and an edit session read the same two values.
 */
function ChargeItemCommentsDrawer({ opened, onClose, row }: ChargeItemCommentsDrawerProps) {
  const providerNotes = row?.editTarget.procedure.providerNotes ?? "";
  const billerNotes = row?.editTarget.procedure.billerNotes ?? "";

  return (
    <Drawer.Root opened={opened} position="right" onClose={onClose}>
      <Drawer.Overlay />
      <Drawer.Content>
        <Drawer.Header mb="lg" px="md" bg="gray.0">
          <Group gap="xs">
            <Drawer.CloseButton size="xl" />
            <Drawer.Title c="dark.9" variant="bold">
              {row ? `Notes — ${row.procedureCode}` : "Notes"}
            </Drawer.Title>
          </Group>
        </Drawer.Header>
        <Drawer.Body>
          <Stack gap="lg" data-testid="charge-item-comments-content">
            <ChargeItemNoteSection
              note={providerNotes}
              title="Practitioner Comments"
              testId="charge-item-comments-practitioner"
            />
            <Divider />
            <ChargeItemNoteSection
              note={billerNotes}
              title="Biller Comments"
              testId="charge-item-comments-biller"
            />
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}

export default ChargeItemCommentsDrawer;
